import './users.scss'

import { Button, Divider, Drawer, Modal, notification, Table } from 'antd'
import axios from 'axios'
import classnames from 'classnames'
import { useEffect, useState } from 'react'
import { Column, Row } from 'react-display-flex'
import useSWR from 'swr'

import { fetcher } from '../../../api'
import { buildTableFilterKeys } from '../../../components/tables/build-table-filter-keys'
import { capitalize } from '../../../formatters'
import { drawerAndModalWidth } from '../../../util/guideline'
import { Tenant } from '../../Tenants/models'
import { CreateUserDrawer } from '../CreateUser/CreateUserDrawer'
import { messages } from '../messages'
import { convertUsersToTableItems, User } from '../models'
import { UserTenants } from '../TenantsAccess'
import { UpdateUser } from '../UpdateUser'
import { buildUsersTableColumns } from './users-table-columns'
import { ResendTemporaryPasswordModal } from '../ResendTemporaryPassword'

export const Users = () => {
  const [userDetails, setUserDetails] = useState(null)
  const [userToExtendPassword, setUserToExtendPassword] = useState(null)
  const [userToResendPassword, setUserToResendPassword] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [activeUserId, setActiveUserId] = useState(null)
  const [filters, setFilters] = useState({ globalRoles: [] })

  const [isCreatingUser, setIsCreatingUser] = useState(false)

  const { data: users, mutate } = useSWR<User[]>('/users', fetcher, { suspense: true })
  const { data: tenants } = useSWR<Tenant[]>('/tenants', fetcher)

  useEffect(() => {
    users &&
      setFilters({
        globalRoles: buildTableFilterKeys(users, 'globalRole', capitalize),
      })
  }, [users])

  const onExtendPassword = (user: User) => {
    setUserToExtendPassword(user)
    setActiveUserId(user.id)
  }

  const onResendPassword = (user: User) => {
    setUserToResendPassword(user)
    setActiveUserId(user.id)
  }

  const onExtendPasswordExpiry = async (userId: number) => {
    setIsDeleting(true)

    try {
      await axios.post(`/users/${userId}/password/extend`)
      await mutate()

      notification.success({ message: messages.common.success, description: messages.extendPasswordExpiry.success })
    } catch (error) {
      notification.error({
        message: messages.common.error,
        description: error.error_message || messages.extendPasswordExpiry.error,
      })
    } finally {
      closeDialogs()
      setIsDeleting(false)
    }
  }

  const onUpdate = (user: User) => {
    setUserDetails(user)
    setActiveUserId(user.id)
  }

  const onCreateOrUpdateSuccess = () => {
    closeDialogs()
    mutate()
  }

  const closeDialogs = () => {
    setIsCreatingUser(false)
    setIsDeleting(false)
    setUserDetails(null)
    setUserToExtendPassword(null)
    setActiveUserId(null)
    setUserToResendPassword(null)
  }

  const usersTableItems = users && tenants ? convertUsersToTableItems({ users, tenants }) : []

  const columns = buildUsersTableColumns({ onUpdate, onExtendPassword, onResendPassword, filters })

  return (
    <Column className="users">
      <Row className="actions" justifyContentEnd alignItemsCenter>
        <Button type="primary" onClick={() => setIsCreatingUser(true)}>
          Create User
        </Button>
      </Row>

      <Table
        rowClassName={(user: User) => classnames({ active: activeUserId === user.id })}
        columns={columns}
        dataSource={usersTableItems}
        loading={!usersTableItems}
        rowKey="id"
        pagination={{ pageSize: 100 }}
        size="small"
        className="users"
      />

      <CreateUserDrawer isVisible={isCreatingUser} onSuccess={onCreateOrUpdateSuccess} />

      <Drawer
        title="Update user"
        placement="right"
        onClose={closeDialogs}
        open={userDetails}
        width={drawerAndModalWidth}
      >
        {userDetails && (
          <>
            <UpdateUser user={userDetails} onSuccess={onCreateOrUpdateSuccess} />
            <Divider />
            <UserTenants user={userDetails} />
          </>
        )}
      </Drawer>

      <Modal
        title="Extend password expiry"
        onCancel={closeDialogs}
        open={userToExtendPassword}
        className="delete-modal-message"
        footer={[
          <div className="footer">
            <Button key="back" onClick={closeDialogs}>
              Cancel
            </Button>
            <Button
              onClick={() => onExtendPasswordExpiry(userToExtendPassword?.id)}
              disabled={isDeleting}
              loading={isDeleting}
              type="primary"
            >
              Extend Password Expiry
            </Button>
          </div>,
        ]}
      >
        {userToExtendPassword && buildExtendPasswordExpirationDateMessage(userToExtendPassword)}
      </Modal>
      <ResendTemporaryPasswordModal
        user={userToResendPassword}
        isVisible={!!userToResendPassword}
        onClose={closeDialogs}
        onSuccess={onCreateOrUpdateSuccess}
      />
    </Column>
  )
}

const buildExtendPasswordExpirationDateMessage = (user: User) =>
  `Are you sure that you want to extend the password expiration date in 7 days?\n
  - Name: ${user.firstName} ${user.surname}\n
  - Email: ${user.email}\n
  It cannot be reverted`
